import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Search, ChevronRight, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV_LINKS = [
  { label: "Inicio", to: "/" },
  { label: "Servicios", to: "/#servicios" },
  { label: "Cómo funciona", to: "/#como-funciona" },
  { label: "Punto Limpio Móvil", to: "/punto-limpio" },
  { label: "Actualidad", to: "/#noticias" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid ? "bg-white shadow-md py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
          <div
            className={`flex items-center justify-center w-11 h-11 rounded-full shrink-0 p-1.5 ${
              solid ? "bg-white shadow-sm" : "bg-white/95"
            }`}
          >
            <img src="/logos/escudo-ayuntamiento.png" alt="Escudo Ayuntamiento de San Fernando de Henares" className="w-full h-full object-contain" />
          </div>
          <div className={`leading-tight ${solid ? "text-municipal" : "text-white"}`}>
            <p className="font-heading font-semibold text-sm sm:text-base">Ayuntamiento de</p>
            <p className="font-heading font-bold text-sm sm:text-base -mt-1">San Fernando de Henares</p>
          </div>
          <div className={`hidden sm:block w-px h-8 mx-1 ${solid ? "bg-municipal/20" : "bg-white/30"}`} />
          <img
            src={solid ? "/logos/valoriza-positivo.svg" : "/logos/valoriza-negativo.svg"}
            alt="Valoriza"
            className="hidden sm:block h-5 w-auto"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <a
              key={link.label}
              href={link.to}
              className={`font-medium text-sm tracking-wide transition-colors hover:text-dorado ${
                solid ? "text-municipal" : "text-white"
              }`}
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link to="/admin">
            <Button
              variant="outline"
              size="icon"
              className={`rounded-full shrink-0 ${
                solid ? "border-municipal/30 text-municipal hover:bg-municipal/10" : "border-white/40 text-white hover:bg-white/10 bg-transparent"
              }`}
              title="Acceso administración"
              data-testid="nav-admin-btn"
            >
              <ShieldCheck className="w-4 h-4" />
            </Button>
          </Link>
          <Link to="/consultar">
            <Button
              variant="ghost"
              className={`font-medium ${solid ? "text-municipal hover:bg-municipal/10" : "text-white hover:bg-white/10"}`}
              data-testid="nav-consultar-btn"
            >
              <Search className="w-4 h-4 mr-2" /> Consultar ticket
            </Button>
          </Link>
          <Link to="/solicitud">
            <Button className="bg-municipal hover:bg-municipal-dark text-white font-medium" data-testid="nav-solicitar-btn">
              Solicitar recogida <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              className={`lg:hidden p-2 rounded-md ${solid ? "text-municipal" : "text-white"}`}
              data-testid="mobile-menu-toggle"
            >
              <Menu className="w-6 h-6" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[280px]">
            <div className="flex flex-col gap-6 mt-10">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.label}
                  href={link.to}
                  onClick={() => setOpen(false)}
                  className="font-medium text-municipal text-lg"
                >
                  {link.label}
                </a>
              ))}
              <Link to="/consultar" onClick={() => setOpen(false)}>
                <Button variant="outline" className="w-full border-municipal text-municipal">
                  Consultar ticket
                </Button>
              </Link>
              <Link to="/solicitud" onClick={() => setOpen(false)}>
                <Button className="w-full bg-municipal text-white">Solicitar recogida</Button>
              </Link>
              <Link to="/admin" onClick={() => setOpen(false)}>
                <Button variant="outline" className="w-full border-municipal text-municipal hover:bg-municipal/10">
                  <ShieldCheck className="w-4 h-4 mr-2" /> Acceso administración
                </Button>
              </Link>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
